/**
 * Phase A: match postgres_changes handler for BlissLiveSync only (no duplicate subscription).
 */
import { supabase } from '@/integrations/supabase/client';
import { injectUserProfile } from '@/utils/userProfileCoordinator';
import {
  BLISSNET_LIVE_MATCH,
  type BlissNetLiveMatchDetail
} from '@/contexts/blissLiveSync/blissnetLiveEvents';

const INJECT_USER_EVENT = 'blissnet-inject-user';

async function fetchOtherUserWithRetry(otherId: string): Promise<Record<string, unknown> | null> {
  const doFetch = async () => {
    const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
    const { data, error } = await rpcPeerUsersForViewer(supabase, [otherId], { includePrefs: false });
    if (error) throw error;
    return data?.[0] ?? null;
  };
  try {
    return await doFetch();
  } catch (err) {
    console.warn('⚡ MATCH: First fetch failed, retrying in 500ms (replication lag):', err);
    await new Promise((r) => setTimeout(r, 500));
    return doFetch();
  }
}

export async function processMatchRealtimeForBlissLive(userId: string, payload: any): Promise<void> {
  const match = payload.new;

  if (!match || match.status !== 'active') return;

  const isUser1 = match.user1_id === userId;
  const isUser2 = match.user2_id === userId;

  if (!isUser1 && !isUser2) return;

  const mySeenAt = isUser1 ? match.user1_seen_at : match.user2_seen_at;

  if (mySeenAt !== null) {
    console.log('⏭️ Realtime Match ignored: Already marked as seen in DB.');
    return;
  }

  const otherId = isUser1 ? match.user2_id : match.user1_id;

  try {
    const profile = await fetchOtherUserWithRetry(otherId);
    const userName = (profile?.name as string) || 'Someone';
    const photoUrls = Array.isArray(profile?.photo_urls) ? profile.photo_urls : [];
    const userPhoto = photoUrls[0] || '';

    injectUserProfile(otherId, {
      id: otherId,
      name: userName,
      photo_urls: photoUrls
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(INJECT_USER_EVENT, {
          detail: { userId: otherId, name: userName, photo_urls: photoUrls }
        })
      );
      window.dispatchEvent(
        new CustomEvent('blissnet-new-match-received', {
          detail: {
            matchId: match.id,
            otherId,
            profile: { id: otherId, name: userName, photo_urls: photoUrls }
          }
        })
      );

      const detail: BlissNetLiveMatchDetail = {
        matchId: match.id,
        otherUserId: otherId,
        otherUserName: userName,
        otherUserPhoto: userPhoto
      };
      window.dispatchEvent(new CustomEvent(BLISSNET_LIVE_MATCH, { detail }));
    }
  } catch (error) {
    console.error('❌ V323 Match realtime (BlissLive) failed:', error);
  }
}
